import { ComponentType } from 'react';

import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import {
  Control,
  Controller,
  FieldValues,
  Path,
  useWatch,
} from 'react-hook-form';

import {
  AccountAutoComplete,
  ApprovalNumberAutoComplete,
  CellLineAutoComplete,
  FertilizationSiteDropdown,
  OptionSetsDropdown,
  SitesAutoComplete,
  SitesDropdown,
  StorageLocationAutoComplete,
} from '@/components';
import { AccountsCombobox } from '@/components/common/accounts-combobox';
import { AdvancedDonorLookup } from '@/components/common/advanced-donor-lookup';
import { AdvancedSemenLookup } from '@/components/common/advanced-semen-lookup';
import { AdvancedSireLookup } from '@/components/common/advanced-sire-lookup';
import { BreedsAutoComplete } from '@/components/common/breeds-auto-complete';
import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { DonorAutoComplete } from '@/components/common/donor-auto-complete';
import { RecipientsAutoComplete } from '@/components/common/recipients-auto-complete';
import { StorageLocationCombobox } from '@/components/common/storage-location-combobox';

type FormFieldProps<T extends FieldValues> = {
  label: string;
  name: Path<T> | string;
  control: Control<T>;
  component: ComponentType<any>;
  required?: boolean;
  disabled?: boolean;
  componentProps?: Record<string, any>;
  lockedDisplayField?: string;
};

export function FormField<T extends FieldValues>({
  label,
  name,
  control,
  component: Component,
  required = false,
  disabled = false,
  componentProps = {},
  lockedDisplayField,
}: FormFieldProps<T>) {
  const lockedDisplayValue = useWatch({
    control,
    name: lockedDisplayField as Path<T>,
    disabled: !lockedDisplayField,
  });

  return (
    <Controller
      name={name as Path<T>}
      control={control}
      render={({ field, fieldState }) => {
        const { ref: _fieldRef, ...fieldWithoutRef } = field;
        const baseProps = {
          ...fieldWithoutRef,
          className: `w-full text-sm ${fieldState.invalid ? 'p-invalid' : ''}`,
          disabled,
          ...componentProps,
        };

        let renderedComponent;

        // locked dropdown w. lockedDisplayField render as InputText instead
        if (Component === Dropdown && disabled && lockedDisplayField) {
          renderedComponent = (
            <InputText
              {...fieldWithoutRef}
              className={`w-full text-sm ${fieldState.invalid ? 'p-invalid' : ''}`}
              disabled={disabled}
              value={lockedDisplayValue || ''}
              readOnly
            />
          );
        } else if (Component === InputText) {
          renderedComponent = (
            <InputText
              {...baseProps}
              value={field.value || ''}
              onChange={(e) => field.onChange(e.target.value)}
            />
          );
        } else if (Component === InputNumber) {
          const { onChange, ...inputNumberProps } = baseProps;
          renderedComponent = (
            <InputNumber
              {...inputNumberProps}
              value={field.value ?? undefined}
              onChange={(e) => {
                field.onChange(e.value);
              }}
            />
          );
        } else if (Component === Calendar) {
          renderedComponent = (
            <Calendar
              {...baseProps}
              value={field.value ? new Date(field.value) : null}
              dateFormat="mm/dd/yy"
              showIcon
              onChange={(e) => field.onChange(e.value)}
            />
          );
        } else if (Component === Dropdown) {
          renderedComponent = (
            <Dropdown
              {...baseProps}
              value={field.value}
              onChange={(e) => field.onChange(e.value)}
              virtualScrollerOptions={{
                itemSize: 25,
              }}
              filter
            />
          );
        } else if (Component === Checkbox) {
          renderedComponent = (
            <Checkbox
              {...baseProps}
              checked={field.value || false}
              onChange={(e) => field.onChange(e.checked)}
            />
          );
        } else if (Component === InputTextarea) {
          renderedComponent = (
            <InputTextarea
              {...baseProps}
              value={field.value || ''}
              onChange={(e) => field.onChange(e.target.value)}
            />
          );
        } else if (
          Component === AccountAutoComplete ||
          Component === StorageLocationAutoComplete ||
          Component === SitesAutoComplete ||
          Component === RecipientsAutoComplete
        ) {
          const autoCompleteProps =
            (Component === SitesAutoComplete ||
              Component === AccountAutoComplete) &&
            typeof field.value === 'string'
              ? { defaultId: field.value }
              : { value: field.value || undefined };

          renderedComponent = (
            <Component
              {...baseProps}
              {...autoCompleteProps}
              onChange={(e: any) => {
                if (!e.value) {
                  field.onChange(null);
                }
              }}
              onSelect={(e: any) => field.onChange(e.value?.id || e.value)}
            />
          );
        } else if (Component === BreedsAutoComplete) {
          renderedComponent = (
            <Component
              {...baseProps}
              onChange={(e: any) => {
                if (typeof e.value === 'object' && e.value !== null) {
                  field.onChange(e.value.id ?? undefined);
                } else if (!e.value) {
                  field.onChange(undefined);
                }
              }}
            />
          );
        } else if (
          Component === CellLineAutoComplete ||
          Component === ApprovalNumberAutoComplete
        ) {
          renderedComponent = (
            <Component
              {...baseProps}
              value={field.value || null}
              onChange={(item: any) => field.onChange(item?.id ?? null)}
              disabled={disabled}
            />
          );
        } else if (
          Component === AdvancedDonorLookup ||
          Component === AdvancedSireLookup ||
          Component === AdvancedSemenLookup ||
          Component === DonorAutoComplete
        ) {
          renderedComponent = (
            <Component
              {...baseProps}
              value={field.value || undefined}
              onChange={(value: any) => field.onChange(value ?? undefined)}
            />
          );
        } else if (
          Component === OptionSetsDropdown ||
          Component === FertilizationSiteDropdown
        ) {
          renderedComponent = (
            <Component
              {...baseProps}
              value={field.value}
              onChange={(e: any) => field.onChange(e.value)}
            />
          );
        } else if (Component === AccountsCombobox) {
          renderedComponent = (
            <AccountsCombobox
              value={field.value || undefined}
              onChange={(id) => field.onChange(id ?? null)}
              className="w-full"
              disabled={disabled}
            />
          );
        } else if (Component === StorageLocationCombobox) {
          renderedComponent = <Component {...baseProps} />;
        } else if (Component === SitesDropdown) {
          const siteValue =
            typeof field.value === 'string' && field.value
              ? { id: field.value }
              : (field.value ?? null);
          renderedComponent = (
            <Component
              {...baseProps}
              value={siteValue}
              onChange={(site: any) => field.onChange(site?.id ?? null)}
              disableUrlSync
            />
          );
        } else {
          renderedComponent = (
            <Component
              {...baseProps}
              value={field.value}
              onChange={field.onChange}
            />
          );
        }

        return (
          <div className="flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              {label}
              {required && <span className="ml-0.5 text-red-500">*</span>}
            </label>
            <div>
              {renderedComponent}
              {fieldState.error && (
                <small className="p-error mt-1 block">
                  {fieldState.error.message}
                </small>
              )}
            </div>
          </div>
        );
      }}
    />
  );
}
